import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useId,
} from "react";

import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import "./CommonFilterInput.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  InputAdornment,
  TextField,
  Popover,
  Box,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AccessibleSpinner from "../../common/AccessibleSpinner";
import SearchIcon from "../../../assets/search.svg";
import { capitalizeFirstLetter } from "../../../utils/capitalize";
import IconButton from "@mui/material/IconButton";

const LABEL_FORMS = {
  Members: { singular: "member", plural: "members" },
  Providers: { singular: "provider", plural: "providers" },
  Pharmacies: { singular: "pharmacy", plural: "pharmacies" },
  Programs: { singular: "program", plural: "programs" },
  Disposition: { singular: "disposition", plural: "dispositions" },
  Status: { singular: "status", plural: "status" },
};

const computeDisplayValue = (label, placeholder, value, multiple, compact) => {
  if (multiple) {
    const selected = Array.isArray(value) ? value : [];
    const count = selected.length;
    if (count === 0) return placeholder;

    if (compact) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontWeight: 530,
          }}
        >
          <span>{label}</span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#E3E8EF",
              color: "#121212",
              borderRadius: "50%",
              width: "auto",
              height: "20px",
              fontSize: "12px",
              fontWeight: 600,
              paddingLeft: "4px",
              paddingRight: "5px",
            }}
          >
            {count}
          </span>
        </div>
      );
    }

    const forms = LABEL_FORMS[label] || {
      singular: label.endsWith("s")
        ? label.slice(0, -1).toLowerCase()
        : label.toLowerCase(),
      plural: label.toLowerCase() + (label.endsWith("s") ? "" : "s"),
    };
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return count === 1
      ? `${count} ${cap(forms.singular)} Selected`
      : `${count} ${cap(forms.plural)} Selected`;
  }
  return value || "";
};

const filterOptions = (options, searchTerm, searchResults) => {
  if (searchTerm && searchResults.length > 0) return searchResults;
  if (searchTerm)
    return options.filter((o) =>
      o.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  return options;
};

const useOutsideClose = (
  ref,
  isOpen,
  searchTerm,
  setInputValue,
  setIsOpen,
  setSearchTerm,
  setSearchResults,
) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [
    isOpen,
    ref,
    setInputValue,
    setIsOpen,
    setSearchTerm,
    setSearchResults,
    searchTerm,
  ]);
};

const SelectAllCheckbox = ({ label, value, options, onSelectAll, allId }) => {
  const total = options.length;
  const valueSet = useMemo(
    () => new Set(Array.isArray(value) ? value : []),
    [value],
  );
  const selectedCount = useMemo(
    () => options.filter((opt) => valueSet.has(opt)).length,
    [options, valueSet],
  );

  const allSelected = total > 0 && selectedCount === total;
  const indeterminate = selectedCount > 0 && selectedCount < total;
  const checkboxId =
    allId || `filter-${String(label).toLowerCase()}-all-checkbox`;
  return (
    <button
      type="button"
      className="dropdown-option select-all"
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        cursor: "pointer",
        marginBottom: "4px",
        borderRadius: "4px",
        backgroundColor: "#403c6f",
        minHeight: "36px",
        boxSizing: "border-box",
        border: "none",
        width: "100%",
        textAlign: "left",
        fontFamily: "inherit",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelectAll();
      }}
    >
      <span
        style={{
          color: "#fff",
          fontSize: "14px",
          fontWeight: 500,
          flexGrow: 1,
        }}
      >
        All
      </span>
      <Checkbox
        checked={allSelected}
        indeterminate={indeterminate}
        size="small"
        slotProps={{
          input: {
            id: checkboxId,
            "aria-label": `${label} - All`,
          },
        }}
        style={{ padding: "4px", marginLeft: "8px", color: "#fff" }}
      />
    </button>
  );
};

SelectAllCheckbox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectAll: PropTypes.func.isRequired,
  allId: PropTypes.string,
};

const customTooltipStyles = {
  tooltip: {
    background: "#403c6f",
    color: "#fff",
    fontSize: "13px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    padding: "8px 12px",
    fontWeight: 400,
    maxWidth: 260,
    lineHeight: 1.5,
    letterSpacing: "0.01em",
  },
  arrow: {
    color: "#403c6f",
  },
};

const toTitleCase = (str) =>
  str.replaceAll(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase(),
  );

const formatCarrierGroupOption = (opt) => {
  // Avoid regex for security: use index-based parsing
  const openIdx = opt.lastIndexOf("(");
  const closeIdx = opt.lastIndexOf(")");
  if (
    openIdx !== -1 &&
    closeIdx !== -1 &&
    openIdx < closeIdx &&
    closeIdx === opt.length - 1
  ) {
    let name = opt.slice(0, openIdx).trim();
    let code = opt.slice(openIdx + 1, closeIdx).trim();
    name = toTitleCase(name);

    const displayName = name.length > 10 ? name.slice(0, 10) + "..." : name;
    let display = displayName;
    let tooltip = name;
    if (code) {
      const displayCode = code.length > 8 ? code.slice(0, 8) + "..." : code;
      display += ` (${displayCode})`;
      tooltip += ` (${code})`;
    }

    return { display, tooltip };
  }

  const name = toTitleCase(opt.trim());
  const display = name.length > 10 ? name.slice(0, 10) + "..." : name;
  return { display, tooltip: name };
};

const NestedFilterOptionsPanel = ({
  open,
  anchorEl,
  onClose,
  options = [],
  selectedValues = [],
  onToggle,
  label,
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
            minWidth: 220,
            maxWidth: 280,
            mt: 0.5,
            p: "8px",
            maxHeight: 320,
            overflowY: "auto",
            zIndex: 1400,
          },
          onMouseDown: (e) => e.stopPropagation(),
          onClick: (e) => e.stopPropagation(),
        },
      }}
    >
      {options.map((opt, idx) => {
        const isSelected = selectedValues.includes(opt);
        const baseLabelId = `nested-filter-option-text-${idx}`;

        return (
          <MenuItem
            key={opt}
            role="menuitemcheckbox"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(opt);
            }}
            sx={{
              width: "100%",
              minHeight: 32,
              height: "auto",
              justifyContent: "space-between",
              p: 0,
              pt: "6px",
              pr: "12px",
              pb: "6px",
              pl: "12px",
              borderRadius: "4px",
              backgroundColor: isSelected ? "#f2f2f2" : "#FFFFFF",
              mb: 0.5,
              "&:hover": {
                backgroundColor: "#F2F2F2",
              },
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <ListItemText
                primary={opt}
                slotProps={{
                  primary: {
                    id: baseLabelId,
                    component: "span",
                    sx: {
                      color: "#0A0A0A",
                      fontSize: "12px",
                      fontWeight: isSelected ? 600 : 400,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    },
                  },
                }}
              />
              <ListItemIcon
                sx={{
                  minWidth: 24,
                  justifyContent: "flex-end",
                  display: "flex",
                }}
              >
                <Checkbox
                  checked={isSelected}
                  size="small"
                  sx={{
                    p: 0,
                    color: "#403C6F",
                    "&.Mui-checked": {
                      color: "#403C6F !important",
                    },
                  }}
                  slotProps={{
                    input: {
                      "aria-labelledby": baseLabelId,
                    },
                  }}
                />
              </ListItemIcon>
            </Box>
          </MenuItem>
        );
      })}
    </Popover>
  );
};

NestedFilterOptionsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  onToggle: PropTypes.func,
  label: PropTypes.string,
};

const OptionRow = memo(
  ({
    option,
    index,
    multiple,
    isSelected,
    label,
    onToggle,
    hasChildren,
    onOpenChildren,
  }) => {
    let optionContent;
    if (label === "Carrier" || label === "Group") {
      const { display, tooltip } = formatCarrierGroupOption(option);
      optionContent = (
        <Tooltip
          title={tooltip}
          arrow
          placement="right"
          slotProps={{
            tooltip: { sx: customTooltipStyles.tooltip },
            arrow: { sx: customTooltipStyles.arrow },
          }}
        >
          <span
            className="option-text"
            style={{
              cursor: "pointer",
              flexGrow: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "180px",
            }}
          >
            {display}
          </span>
        </Tooltip>
      );
    } else if (label === "Assignee" || label === "Drug List") {
      optionContent = (
        <Tooltip
          title={option}
          arrow
          placement="right"
          slotProps={{
            tooltip: { sx: customTooltipStyles.tooltip },
            arrow: { sx: customTooltipStyles.arrow },
          }}
        >
          <span
            className="option-text"
            style={{
              cursor: "pointer",
              flexGrow: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "180px",
            }}
          >
            {option}
          </span>
        </Tooltip>
      );
    } else {
      optionContent = (
        <span
          className="option-text"
          style={{
            cursor: "pointer",
            flexGrow: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "180px",
          }}
        >
          {label === "Program" ||
          label === "Programs" ||
          label === "Recent Activity" ||
          label === "Filter by Recent Activity"
            ? option
            : capitalizeFirstLetter(option)}
        </span>
      );
    }

    const Component = multiple ? "div" : "button";
    return (
      <Component
        key={option}
        className={`dropdown-option ${isSelected ? "selected" : ""} ${
          index < 99999 ? "with-underline" : ""
        }`}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          border: "none",
          background: "none",
          width: "100%",
          textAlign: "left",
          fontFamily: "inherit",
          padding: "8px 12px",
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (hasChildren) {
            onOpenChildren(e.currentTarget);
          } else {
            onToggle(option);
          }
        }}
        type={multiple ? undefined : "button"}
        role={multiple ? undefined : "option"}
        aria-selected={multiple ? undefined : isSelected}
      >
        {optionContent}
        {hasChildren && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChildren(e.currentTarget);
            }}
            sx={{ p: "2px", ml: "auto", mr: "4px", color: "#fff" }}
          >
            <KeyboardArrowRightIcon style={{ fontSize: 18 }} />
          </IconButton>
        )}
        {multiple && !hasChildren && (
          <Checkbox
            checked={isSelected}
            size="small"
            onClick={(e) => {
              if (hasChildren) e.stopPropagation();
            }}
            onChange={(e) => {
              e.stopPropagation();
              onToggle(option);
            }}
            slotProps={{
              input: {
                id: `filter-${label.toLowerCase()}-checkbox-${index}`,
                "aria-label": option,
              },
            }}
            style={{ padding: "4px", marginLeft: hasChildren ? "0px" : "auto" }}
          />
        )}
      </Component>
    );
  },
);
OptionRow.propTypes = {
  option: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  multiple: PropTypes.bool,
  isSelected: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  hasChildren: PropTypes.bool,
  onOpenChildren: PropTypes.func,
};
OptionRow.displayName = "OptionRow";

const getSearchPlaceholder = (label) => {
  const map = {
    Members: "Search member",
    Member: "Search member",
    Providers: "Search provider",
    Provider: "Search provider",
    Pharmacies: "Search pharmacy",
    Pharmacy: "Search pharmacy",
    Programs: "Search program",
    Program: "Search program",
  };
  return map[label] || "Search...";
};

const useFilterSelect = ({
  multiple,
  options,
  value,
  onChange,
  onSearch,
  onOpen,
  onLoadMore,
  isLoading = false,
  hasMore = false,
  filterType,
  autoPrefetch = true,
  searchValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(100);
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useOutsideClose(
    dropdownRef,
    isOpen,
    searchTerm,
    setInputValue,
    setIsOpen,
    setSearchTerm,
    setSearchResults,
  );

  // Sync inputValue with searchValue prop when it changes
  useEffect(() => {
    if (searchValue !== undefined) {
      setInputValue(searchValue);
      if (searchValue === "") {
        setSearchTerm("");
        setSearchResults([]);
      } else {
        setSearchTerm(searchValue);
        setSearchResults([]);
      }
    }
  }, [searchValue]);

  const currentValues = useMemo(
    () => (multiple && Array.isArray(value) ? value : []),
    [multiple, value],
  );
  const clearAll = useCallback(() => {
    if (multiple) {
      if (Array.isArray(value) && value.length > 0) {
        onChange?.([]);
      }
    } else if (value) {
      onChange?.("");
    }
    setSearchTerm("");
    setInputValue("");

    onSearch?.("", filterType);
  }, [multiple, value, onChange, onSearch, filterType]);

  const toggleDropdown = useCallback(
    (e) => {
      if (e?.target?.closest(".dropdown-icon")) clearAll();
      if (isOpen && !searchTerm) setInputValue("");
      setIsOpen((o) => {
        const next = !o;
        if (next) {
          onOpen?.(filterType);
        } else {
          setVisibleLimit(100);
        }
        return next;
      });
    },
    [clearAll, isOpen, searchTerm, onOpen, filterType],
  );

  const selectAll = useCallback(() => {
    // Only operate on currently filtered options (search results)
    const filtered = filterOptions(options, searchTerm, searchResults);
    if (filtered.length === 0) return;
    const allFilteredSelected = filtered.every((opt) =>
      currentValues.includes(opt),
    );
    if (allFilteredSelected) {
      // Deselect only the filtered options, keep others selected
      const newValues = currentValues.filter((v) => !filtered.includes(v));
      onChange?.(newValues);
    } else {
      // Select all filtered options, keep others selected
      const newSet = new Set([...currentValues, ...filtered]);
      onChange?.(Array.from(newSet));
    }
  }, [options, searchTerm, searchResults, currentValues, onChange]);

  const commitSearch = useCallback(() => {
    const term = inputValue.trim();
    if (term) {
      setSearchTerm(term);
      const res = onSearch?.(term, filterType);
      if (res && typeof res.then === "function") {
        setIsSearching(true);
        res.finally(() => setIsSearching(false));
      } else {
        setIsSearching(false);
      }
    } else {
      setSearchTerm("");
      setSearchResults([]);
      onSearch?.("", filterType);
    }
    setVisibleLimit(100);
  }, [inputValue, onSearch, filterType]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitSearch();
      }
    },
    [commitSearch],
  );

  const handleInputChange = useCallback(
    (e) => {
      const val = e.target.value;
      setInputValue(val);
      if (!val.trim()) {
        setSearchTerm("");
        setSearchResults([]);
        onSearch?.("", filterType);
      }
      setVisibleLimit(100);
    },
    [onSearch, filterType],
  );

  const handleOptionClick = useCallback(
    (option) => {
      if (multiple) {
        const newValues = currentValues.includes(option)
          ? currentValues.filter((v) => v !== option)
          : [...currentValues, option];
        onChange?.(newValues);
        if (window.innerWidth <= 768) setIsOpen(false);
      } else {
        onChange?.(option);
        setIsOpen(false);
      }
    },
    [multiple, onChange, currentValues],
  );
  const filteredOptions = useMemo(() => {
    const list = filterOptions(options, searchTerm, searchResults);
    if (!value || (multiple && (!Array.isArray(value) || value.length === 0))) {
      return list;
    }
    const selected = [];
    const unselected = [];
    if (multiple) {
      const currentValues = new Set(value);
      const listSet = new Set(list);
      // [...value].reverse() ensures the most recently selected is first
      selected.push(...[...value].reverse().filter((opt) => listSet.has(opt)));
      for (const opt of list) {
        if (!currentValues.has(opt)) unselected.push(opt);
      }
    } else {
      for (const opt of list) {
        if (opt === value) selected.push(opt);
        else unselected.push(opt);
      }
    }
    return [...selected, ...unselected];
  }, [options, searchTerm, searchResults, multiple, value]);

  useEffect(() => {
    if (!isOpen) return;
    const menuEl = dropdownMenuRef.current;
    const listEl = optionsRef.current;
    if (!menuEl && !listEl) return;
    const onScroll = (el) => {
      if (loadingMoreRef.current) return;
      if (isSearching || isLoading) return;
      const threshold = 100;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        if (visibleLimit < filteredOptions.length) {
          setVisibleLimit((prev) => prev + 100);
          return;
        }

        if (!hasMore) return;

        loadingMoreRef.current = true;
        void Promise.resolve(onLoadMore?.(filterType)).finally(() => {
          loadingMoreRef.current = false;
        });
      }
    };
    const menuHandler = () => onScroll(menuEl);
    const listHandler = () => onScroll(listEl);
    if (menuEl)
      menuEl.addEventListener("scroll", menuHandler, { passive: true });
    if (listEl)
      listEl.addEventListener("scroll", listHandler, { passive: true });
    return () => {
      if (menuEl) menuEl.removeEventListener("scroll", menuHandler);
      if (listEl) listEl.removeEventListener("scroll", listHandler);
    };
  }, [
    isOpen,
    isSearching,
    isLoading,
    hasMore,
    onLoadMore,
    filterType,
    visibleLimit,
    filteredOptions.length,
  ]);

  useEffect(() => {
    if (!autoPrefetch) return;
    if (!isOpen) return;
    const menuEl = dropdownMenuRef.current;
    const listEl = optionsRef.current;
    if (!menuEl && !listEl) return;
    if (loadingMoreRef.current) return;
    if (isLoading) return;
    if (!hasMore) return;
    const menuNeeds = menuEl
      ? menuEl.scrollHeight <= menuEl.clientHeight
      : false;
    const listNeeds = listEl
      ? listEl.scrollHeight <= listEl.clientHeight
      : false;
    if (menuNeeds || listNeeds) {
      loadingMoreRef.current = true;
      void Promise.resolve(onLoadMore?.(filterType)).finally(() => {
        loadingMoreRef.current = false;
      });
    }
  }, [
    autoPrefetch,
    isOpen,
    hasMore,
    isLoading,
    onLoadMore,
    filterType,
    filteredOptions.length,
  ]);
  return {
    dropdownRef,
    optionsRef,
    dropdownMenuRef,
    isOpen,
    toggleDropdown,
    inputValue,
    handleInputChange,
    handleKeyDown,
    commitSearch,
    isSearching,
    isLoading,
    filteredOptions,
    handleOptionClick,
    selectAll,
    hasMore,
    visibleLimit,
  };
};

const MemoizedOptionsList = memo(
  ({
    filteredOptions,
    multiple,
    label,
    value,
    onToggle,
    nestedOptions,
    onOpenChildren,
  }) => {
    return (
      <>
        {filteredOptions.map((option, idx) => {
          const hasChildren =
            nestedOptions &&
            typeof nestedOptions === "object" &&
            !Array.isArray(nestedOptions) &&
            Object.hasOwn(nestedOptions, option);

          return (
            <OptionRow
              key={option}
              option={option}
              index={idx}
              multiple={multiple}
              label={label}
              isSelected={
                multiple
                  ? Array.isArray(value) && value.includes(option)
                  : value === option
              }
              onToggle={onToggle}
              hasChildren={hasChildren}
              onOpenChildren={(anchorEl) => onOpenChildren?.(option, anchorEl)}
            />
          );
        })}
      </>
    );
  },
);
MemoizedOptionsList.propTypes = {
  filteredOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  multiple: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onToggle: PropTypes.func.isRequired,
  nestedOptions: PropTypes.object,
  onOpenChildren: PropTypes.func,
};
MemoizedOptionsList.displayName = "MemoizedOptionsList";

const FilterSelect = ({
  label,
  placeholder,
  value,
  multiple,
  options,
  onChange,
  onSearch,
  onOpen,
  onLoadMore,
  isLoading,
  hasMore,
  filterType,
  autoPrefetch = true,
  instanceId,
  compact,
  searchValue,
  showLabel = true,
  nestedOptions,
  nestedValues = [],
  onNestedToggle,
}) => {
  const {
    dropdownRef,
    optionsRef,
    dropdownMenuRef,
    isOpen,
    toggleDropdown,
    inputValue,
    handleInputChange,
    handleKeyDown,
    commitSearch,
    isSearching,
    isLoading: isLoadingFromHook,
    filteredOptions,
    handleOptionClick,
    selectAll,
    visibleLimit,
  } = useFilterSelect({
    multiple,
    options,
    value,
    onChange,
    onSearch,
    onOpen,
    onLoadMore,
    isLoading,
    hasMore,
    filterType,
    autoPrefetch,
    searchValue,
  });

  const uidHook = useId();
  const uid = instanceId || uidHook;
  const idBase = `filter-${label.toLowerCase()}-${uid}`;
  const displayId = `${idBase}-input-display`;
  const menuId = `${idBase}-menu`;
  const labelId = `${idBase}-label`;
  const allCheckboxId = `${idBase}-all-checkbox`;

  const [activeParent, setActiveParent] = useState(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setActiveParent(null);
      setNestedAnchorEl(null);
    }
  }, [isOpen]);

  const displayValue = useMemo(() => {
    if (label === "Recent Activity" || label === "Recent Activity Outcome") {
      const selectedOutcomes = Array.isArray(value) ? value : [];
      const selectedReasons = Array.isArray(nestedValues) ? nestedValues : [];

      const outcomesToCount = selectedOutcomes.filter((outcome) => {
        const children = nestedOptions?.[outcome] || [];
        const hasAnyChildSelected = children.some((child) =>
          selectedReasons.includes(child),
        );
        return !hasAnyChildSelected;
      });

      const totalCount = outcomesToCount.length + selectedReasons.length;
      if (totalCount === 0) return placeholder;

      if (compact) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 530,
            }}
          >
            <span>{label}</span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#E3E8EF",
                color: "#121212",
                borderRadius: "50%",
                width: "auto",
                height: "20px",
                fontSize: "12px",
                fontWeight: 600,
                paddingLeft: "4px",
                paddingRight: "5px",
              }}
            >
              {totalCount}
            </span>
          </div>
        );
      }
      return `${totalCount} Selected`;
    }
    return computeDisplayValue(label, placeholder, value, multiple, compact);
  }, [
    label,
    placeholder,
    value,
    nestedValues,
    nestedOptions,
    multiple,
    compact,
  ]);

  const searchPlaceholder = getSearchPlaceholder(label);

  const handleOpenChildren = (clickedOption, anchorElement) => {
    setActiveParent(clickedOption);
    setNestedAnchorEl(anchorElement);
  };

  const handleParentToggle = (parentOption) => {
    if (nestedOptions) {
      const isParentSelected =
        Array.isArray(value) && value.includes(parentOption);
      const nextOutcomes = isParentSelected
        ? value.filter((v) => v !== parentOption)
        : [...(value || []), parentOption];

      const children = nestedOptions[parentOption] || [];
      let nextReasons = [...nestedValues];
      if (isParentSelected) {
        nextReasons = nextReasons.filter((r) => !children.includes(r));
      } else {
        children.forEach((child) => {
          if (!nextReasons.includes(child)) {
            nextReasons.push(child);
          }
        });
      }
      onChange?.(nextOutcomes, nextReasons);
    } else {
      handleOptionClick(parentOption);
    }
  };

  const handleSelectAllWithChildren = useCallback(() => {
    const filtered = filterOptions(options, inputValue.trim(), []);
    if (filtered.length === 0) return;
    const currentValues = Array.isArray(value) ? value : [];
    const allFilteredSelected = filtered.every((opt) =>
      currentValues.includes(opt),
    );

    if (allFilteredSelected) {
      const newValues = currentValues.filter((v) => !filtered.includes(v));
      let newReasons = [...nestedValues];
      filtered.forEach((parent) => {
        const children = nestedOptions[parent] || [];
        newReasons = newReasons.filter((r) => !children.includes(r));
      });
      onChange?.(newValues, newReasons);
    } else {
      const newSet = new Set([...currentValues, ...filtered]);
      const newValues = Array.from(newSet);
      let newReasons = [...nestedValues];
      filtered.forEach((parent) => {
        const children = nestedOptions[parent] || [];
        children.forEach((child) => {
          if (!newReasons.includes(child)) {
            newReasons.push(child);
          }
        });
      });
      onChange?.(newValues, newReasons);
    }
  }, [options, inputValue, value, nestedValues, nestedOptions, onChange]);

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (nestedOptions) {
      onChange?.([], []);
      return;
    }
    if (multiple) {
      onChange?.([]);
      return;
    }
    onChange?.("");
  };

  return (
    <div className="common-filter-input" ref={dropdownRef}>
      {showLabel && (
        <label className="filter-label" htmlFor={displayId} id={labelId}>
          {label}
        </label>
      )}
      <div className="filter-input-container">
        <button
          id={displayId}
          className="filter-input"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-controls={menuId}
          aria-label={`${label}: ${displayValue}`}
          onClick={toggleDropdown}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            paddingRight: { xs: "32px", md: "12px" },
            background: "#fff",
            border: "1px solid #e8e8e9",
            borderRadius: "6px",
            width: "100%",
            textAlign: "left",
            fontFamily: "inherit",
          }}
        >
          {displayValue}
        </button>
        {isOpen ? (
          <CloseIcon
            className="dropdown-icon"
            style={{
              cursor: "pointer",
              fontSize: "20px",
              color: "#666",
              pointerEvents: "auto",
            }}
            onClick={handleClear}
          />
        ) : (
          <ExpandMoreIcon
            className="dropdown-icon"
            viewBox="0 0 24 24"
            style={{ transition: "transform 0.2s" }}
          />
        )}
        {isOpen && (
          <section
            id={menuId}
            className={`dropdown-menu ${multiple ? "multiple" : ""}`}
            aria-label={`${label} filter options`}
            ref={dropdownMenuRef}
          >
            <div className="options-container" ref={optionsRef}>
              <div className="search-option">
                <TextField
                  type="text"
                  placeholder={searchPlaceholder}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  className="search-option-input"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#92A0B3",
                      },
                    },
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {inputValue && inputValue.length > 0 && (
                              <IconButton
                                aria-label="clear filter search"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInputChange({ target: { value: "" } });
                                }}
                                sx={{ padding: "4px" }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}

                            {isSearching ? (
                              <AccessibleSpinner size={20} label="Searching" />
                            ) : (
                              <IconButton
                                size="small"
                                onClick={commitSearch}
                                aria-label="Execute search"
                                sx={{ padding: "4px", marginRight: "8px" }}
                              >
                                <img
                                  src={SearchIcon}
                                  alt=""
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                  }}
                                />
                              </IconButton>
                            )}
                          </div>
                        </InputAdornment>
                      ),
                      style: { paddingRight: "8px" },
                      sx: { "&:focus": { outline: "none", boxShadow: "none" } },
                    },
                  }}
                />
              </div>
              {multiple && filteredOptions.length > 0 && (
                <SelectAllCheckbox
                  label={label}
                  value={value}
                  options={filteredOptions}
                  onSelectAll={
                    nestedOptions ? handleSelectAllWithChildren : selectAll
                  }
                  allId={allCheckboxId}
                />
              )}
              <MemoizedOptionsList
                filteredOptions={filteredOptions.slice(0, visibleLimit)}
                multiple={multiple}
                label={label}
                value={value}
                onToggle={handleParentToggle}
                nestedOptions={nestedOptions}
                onOpenChildren={handleOpenChildren}
              />
              {isLoadingFromHook || isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "8px 0",
                  }}
                >
                  <AccessibleSpinner
                    size={20}
                    label="Loading options"
                    sx={{ color: "#fff" }}
                  />
                </div>
              ) : (
                filteredOptions.length === 0 && (
                  <div className="no-options">No options available</div>
                )
              )}
            </div>
          </section>
        )}
      </div>
      {nestedOptions &&
        activeParent &&
        Array.isArray(nestedOptions[activeParent]) && (
          <NestedFilterOptionsPanel
            open={isOpen && Boolean(activeParent)}
            anchorEl={nestedAnchorEl}
            onClose={() => {
              setActiveParent(null);
              setNestedAnchorEl(null);
            }}
            options={nestedOptions[activeParent]}
            selectedValues={nestedValues}
            onToggle={(subOpt) => onNestedToggle?.(activeParent, subOpt)}
            label={label}
          />
        )}
    </div>
  );
};

FilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onOpen: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  filterType: PropTypes.string,
  autoPrefetch: PropTypes.bool,
  instanceId: PropTypes.string,
  compact: PropTypes.bool,
  searchValue: PropTypes.string,
  showLabel: PropTypes.bool,
  nestedOptions: PropTypes.object,
  nestedValues: PropTypes.arrayOf(PropTypes.string),
  onNestedToggle: PropTypes.func,
};

const FilterText = ({ label, placeholder, value, onChange, instanceId }) => {
  const uidHook = useId();
  const uid = instanceId || uidHook;
  const idBase = `filter-${label.toLowerCase()}-${uid}`;
  const inputId = `${idBase}-text-input`;
  return (
    <div className="common-filter-input">
      <label className="filter-label" htmlFor={inputId}>
        {label}
      </label>
      <div className="filter-input-container">
        <div style={{ position: "relative", width: "100%" }}>
          <input
            type="text"
            className="filter-input"
            placeholder={placeholder}
            value={value}
            id={inputId}
            onChange={(e) => onChange?.(e.target.value)}
            style={{ padding: "8px 12px" }}
          />
          {value && value.length > 0 && (
            <IconButton
              className="clear-text-icon"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.("");
              }}
              aria-label={`Clear ${label} search`}
              sx={{
                position: "absolute",
                right: 32,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                padding: "4px",
              }}
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          )}
        </div>
        <svg
          aria-hidden="true"
          className="dropdown-icon"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </div>
    </div>
  );
};

FilterText.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  instanceId: PropTypes.string,
};

const CommonFilterInput = (props) => {
  const { type = "text" } = props;
  return type === "select" ? (
    <FilterSelect {...props} />
  ) : (
    <FilterText {...props} />
  );
};

export default CommonFilterInput;

CommonFilterInput.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf(["text", "select"]),
  multiple: PropTypes.bool,
  onSearch: PropTypes.func,
  onOpen: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  filterType: PropTypes.string,
  autoPrefetch: PropTypes.bool,
  instanceId: PropTypes.string,
  compact: PropTypes.bool,
  searchValue: PropTypes.string,
  showLabel: PropTypes.bool,
};
